import React, { useMemo } from 'react';
import { useLocale } from '../../contexts/LocaleContext';
interface HiveGridProps {
  filled: number;
  total: number;
  columns?: number;
}

/**
 * Minha Colmeia — honeycomb that fills from the centre outward.
 * Filled cells are Deep Green; the newest cell is Honey Gold.
 */
export function HiveGrid({
  filled,
  total,
  columns = 10
}: HiveGridProps) {
  const {
    t
  } = useLocale();
  const size = 14;
  const w = Math.sqrt(3) * size;
  const rows = Math.ceil(total / columns);
  const cells = useMemo(() => {
    const list: {
      x: number;
      y: number;
      d: number;
    }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < columns; c++) {
        if (list.length >= total) break;
        const x = w / 2 + c * w + (r % 2 ? w / 2 : 0) + 1;
        const y = size + r * size * 1.5 + 1;
        list.push({
          x,
          y,
          d: 0
        });
      }
    }
    const cx = (columns * w + w / 2) / 2;
    const cy = (rows * size * 1.5 + size / 2) / 2;
    const order = list.map((cell, index) => ({
      index,
      d: Math.hypot(cell.x - cx, (cell.y - cy) * 1.1)
    })).sort((a, b) => a.d - b.d);
    const rank = new Array<number>(list.length);
    order.forEach((item, i) => {
      rank[item.index] = i;
    });
    return list.map((cell, index) => ({
      ...cell,
      d: rank[index]
    }));
  }, [rows, columns, total, w]);
  const width = columns * w + w / 2 + 2;
  const height = rows * size * 1.5 + size / 2 + 2;
  const hex = (x: number, y: number) => Array.from({
    length: 6
  }, (_, i) => {
    const angle = Math.PI / 180 * (60 * i - 90);
    return `${(x + (size - 1.2) * Math.cos(angle)).toFixed(2)},${(y + (size - 1.2) * Math.sin(angle)).toFixed(2)}`;
  }).join(' ');
  return <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label={t('eng.hive.aria', {
    filled
  })} data-id="element-709" data-mp="14yhi5z2vmxxf">
      {cells.map((cell, index) => {
      const isFilled = cell.d < filled;
      const isNewest = cell.d === filled - 1;
      return <polygon key={index} points={hex(cell.x, cell.y)} fill={isNewest ? 'var(--bee-gold)' : isFilled ? 'var(--bee-deep)' : 'transparent'} stroke={isFilled ? 'none' : 'rgba(16,42,42,0.16)'} strokeWidth={1} opacity={isFilled && !isNewest ? 0.55 + 0.45 * (1 - cell.d / Math.max(filled, 1)) : 1} data-id="element-710" data-mp="33q2mmvl6pg17" data-mpi={index} />;
    })}
    </svg>;
}